import {
  Alert,
  Box,
  Button,
  Fade,
  IconButton,
  Link as MuiLink,
  Paper,
  TextField,
  Typography,
  keyframes,
} from '@mui/material';
import { CREAM, GREEN_BP, GREEN_DARK } from 'theme';
import { ChatMessage } from 'chatbotApi';
import { FormEvent, KeyboardEvent, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { alpha, darken } from '@mui/material/styles';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import ApiContext from 'contexts/apiContext';
import StoreContext from 'contexts/storeContext';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useChatbotEnabled } from 'hooks';
import { useLocation, useNavigate } from 'react-router-dom';

const useChatbotUrl = () =>
  !!(process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_HTTP_PROTOCOL);

const WELCOME_MESSAGE =
  '¡Hola! Estoy para ayudarte con  Buen Provecho — packs, pedidos, horarios, facturación y más. ¿Qué querés saber?';

const USER_BUBBLE_BG = darken(GREEN_BP, 0.18);
const CHAT_SURFACE = '#F6F5F1';
const ASSISTANT_BUBBLE = '#FFFFFF';
const INPUT_MAX_CHARS = 200;
const INPUT_MAX_ROWS = 5;

const dotPulse = keyframes`
  0%, 80%, 100% { opacity: 0.35; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
`;

const launcherPop = keyframes`
  0% { transform: scale(0.55); opacity: 0; }
  55% { transform: scale(1.1); opacity: 1; }
  75% { transform: scale(0.97); }
  100% { transform: scale(1); }
`;

const APP_ROUTE_LABELS: Record<string, string> = {
  '/app/home': 'Inicio',
  '/app/packs': 'Packs',
  '/app/packs/images': 'Imágenes',
  '/app/packs/shared': 'Packs compartidos',
  '/app/orders': 'Pedidos actuales',
  '/app/order-history': 'Historial de pedidos',
  '/app/store': 'Tu tienda',
  '/app/store/reviews': 'Reseñas',
  '/app/times': 'Horarios',
  '/app/billing': 'Facturación',
  '/app/billing/receipts': 'Recibos y notas de crédito',
  '/app/help': 'Ayuda',
  '/app/notifications': 'Notificaciones',
};

const ALLOWED_INTERNAL_PATHS = new Set(Object.keys(APP_ROUTE_LABELS));

function normalizeAppPath(url: string): string {
  const path = url.split(/[?#]/)[0];
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

function isAllowedInternalLink(url: string): boolean {
  if (url.startsWith('//') || !url.startsWith('/app/')) {
    return false;
  }
  return ALLOWED_INTERNAL_PATHS.has(normalizeAppPath(url));
}

function isExternalHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function friendlyLinkLabel(label: string, url: string): string {
  if (!label.startsWith('/') && label !== url) {
    return label;
  }
  return APP_ROUTE_LABELS[url] ?? 'ir a la sección';
}

function renderInlineMarkdown(
  text: string,
  onLink: (url: string) => void,
): ReactNode[] {
  const parts: ReactNode[] = [];
  // Matches **bold** or [label](url) in a single pass.
  const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1] !== undefined) {
      parts.push(
        <Box component='strong' key={key++} sx={{ fontWeight: 700 }}>
          {match[1]}
        </Box>,
      );
    } else {
      const url = match[3];
      const label = friendlyLinkLabel(match[2], url);
      const internalPath = isAllowedInternalLink(url) ? normalizeAppPath(url) : null;

      if (!internalPath && !isExternalHttpUrl(url)) {
        parts.push(label);
      } else {
        parts.push(
          <MuiLink
            key={key++}
            component='button'
            type='button'
            onClick={() => {
              if (internalPath) {
                onLink(internalPath);
              } else {
                window.open(url, '_blank', 'noopener,noreferrer');
              }
            }}
            sx={{
              color: GREEN_BP,
              fontWeight: 600,
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              verticalAlign: 'baseline',
              font: 'inherit',
              p: 0,
              cursor: 'pointer',
              '&:hover': { color: darken(GREEN_BP, 0.12) },
            }}
          >
            {label}
          </MuiLink>,
        );
      }
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

function AssistantAvatar({ size = 28 }: { size?: number }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: alpha(GREEN_BP, 0.12),
        color: GREEN_BP,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        mt: 0.25,
      }}
    >
      <SupportAgentIcon sx={{ fontSize: size * 0.58 }} />
    </Box>
  );
}

function MessageBubble({
  role,
  content,
  onLink,
}: ChatMessage & { onLink: (url: string) => void }) {
  const isUser = role === 'user';

  if (isUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <Box
          sx={{
            maxWidth: '82%',
            px: 1.75,
            py: 1.25,
            borderRadius: '18px 18px 4px 18px',
            bgcolor: USER_BUBBLE_BG,
            color: CREAM,
            boxShadow: `0 2px 8px ${alpha(GREEN_DARK, 0.12)}`,
          }}
        >
          <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: '0.875rem' }}>
            {content}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', width: '100%' }}>
      <AssistantAvatar />
      <Box
        sx={{
          maxWidth: 'calc(100% - 40px)',
          px: 1.75,
          py: 1.25,
          borderRadius: '4px 18px 18px 18px',
          bgcolor: ASSISTANT_BUBBLE,
          color: GREEN_DARK,
          border: `1px solid ${alpha(GREEN_BP, 0.1)}`,
          boxShadow: `0 1px 4px ${alpha(GREEN_DARK, 0.06)}`,
        }}
      >
        <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, fontSize: '0.875rem' }}>
          {renderInlineMarkdown(content, onLink)}
        </Typography>
      </Box>
    </Box>
  );
}

function TypingIndicator() {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <AssistantAvatar />
      <Box
        sx={{
          px: 1.75,
          py: 1.5,
          borderRadius: '4px 18px 18px 18px',
          bgcolor: ASSISTANT_BUBBLE,
          border: `1px solid ${alpha(GREEN_BP, 0.1)}`,
          display: 'flex',
          gap: 0.6,
          alignItems: 'center',
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: alpha(GREEN_BP, 0.55),
              animation: `${dotPulse} 1.2s ease-in-out infinite`,
              animationDelay: `${i * 0.15}s`,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export default function ChatbotWidget() {
  const chatbotConfigured = useChatbotUrl();
  const navigate = useNavigate();
  const location = useLocation();
  const { api, currentUser } = useContext(ApiContext);
  const { store } = useContext(StoreContext);
  const chatbotEnabled = useChatbotEnabled(store, currentUser);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyLimitReached, setHistoryLimitReached] = useState(false);
  const [launcherPopKey, setLauncherPopKey] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const skipLauncherPopRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [open, messages, loading, scrollToBottom]);

  useEffect(() => {
    if (skipLauncherPopRef.current) {
      skipLauncherPopRef.current = false;
      return;
    }
    setLauncherPopKey((key) => key + 1);
  }, [location.pathname]);

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    const userMsg: ChatMessage = { role: 'user', content: text };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setError(null);
    setLoading(true);

    try {
      const { content, history_limit_reached: limitReached } = await api.chatbot.chat(store.id, nextMessages);
      if (!content?.trim()) {
        setError('El asistente no devolvió una respuesta. Intentá de nuevo.');
        return;
      }
      setHistoryLimitReached(limitReached);
      setMessages([...nextMessages, { role: 'assistant', content }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al enviar el mensaje';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  const handleLink = useCallback(
    (url: string) => {
      skipLauncherPopRef.current = true;
      navigate(url);
    },
    [navigate],
  );

  const handleResetConversation = useCallback(() => {
    setMessages([]);
    setHistoryLimitReached(false);
    setError(null);
    setInput('');
    inputRef.current?.focus();
  }, []);

  if (!chatbotConfigured || !chatbotEnabled) {
    return null;
  }

  return (
    <>
      <Fade in={open}>
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: { xs: 84, sm: 96 },
            right: { xs: 12, sm: 24 },
            width: { xs: 300, sm: 400 },
            maxWidth: { xs: 'calc(100vw - 56px)', sm: 'calc(100vw - 32px)' },
            height: { xs: 400, sm: 560 },
            maxHeight: { xs: 'calc(100vh - 120px)', sm: 'calc(100vh - 112px)' },
            display: open ? 'flex' : 'none',
            flexDirection: 'column',
            zIndex: (theme) => theme.zIndex.modal,
            overflow: 'hidden',
            borderRadius: { xs: '16px', sm: '20px' },
            border: `1px solid ${alpha(GREEN_BP, 0.14)}`,
            boxShadow: `0 20px 50px ${alpha(GREEN_DARK, 0.22)}, 0 4px 12px ${alpha(GREEN_DARK, 0.08)}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1.25, sm: 1.75 },
              bgcolor: GREEN_BP,
              color: CREAM,
            }}
          >
            <Box
              sx={{
                width: { xs: 34, sm: 40 },
                height: { xs: 34, sm: 40 },
                borderRadius: { xs: '10px', sm: '12px' },
                bgcolor: alpha(CREAM, 0.15),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AutoAwesomeIcon fontSize='small' />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant='subtitle2' fontWeight={700} lineHeight={1.3} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Asistente Buen Provecho
              </Typography>
              <Typography variant='caption' sx={{ opacity: 0.85, display: { xs: 'none', sm: 'block' } }}>
                En línea · Respuestas sobre la plataforma
              </Typography>
            </Box>
            <IconButton
              size='small'
              onClick={() => setOpen(false)}
              aria-label='Minimizar chat'
              sx={{
                color: 'inherit',
                bgcolor: alpha(CREAM, 0.1),
                '&:hover': { bgcolor: alpha(CREAM, 0.2) },
              }}
            >
              <RemoveIcon fontSize='small' />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1.5, sm: 2 },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1.25, sm: 1.5 },
              bgcolor: CHAT_SURFACE,
              backgroundImage: `radial-gradient(${alpha(GREEN_BP, 0.04)} 1px, transparent 1px)`,
              backgroundSize: '16px 16px',
            }}
          >
            <MessageBubble role='assistant' content={WELCOME_MESSAGE} onLink={handleLink} />
            {messages.map((m, i) => (
              <MessageBubble key={i} role={m.role} content={m.content} onLink={handleLink} />
            ))}
            {historyLimitReached && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                <Button
                  size='small'
                  variant='outlined'
                  onClick={handleResetConversation}
                  sx={{
                    borderColor: alpha(GREEN_BP, 0.45),
                    color: GREEN_BP,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '10px',
                    px: 2,
                    '&:hover': {
                      borderColor: GREEN_BP,
                      bgcolor: alpha(GREEN_BP, 0.06),
                    },
                  }}
                >
                  Nueva conversación
                </Button>
              </Box>
            )}
            {loading && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </Box>

          {error && (
            <Alert
              severity='error'
              sx={{ mx: 2, mt: 1, borderRadius: 2, py: 0 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Box
            component='form'
            onSubmit={handleSubmit}
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1.25, sm: 1.5 },
              bgcolor: '#FFF',
              borderTop: `1px solid ${alpha(GREEN_BP, 0.1)}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 1,
                p: 0.75,
                borderRadius: '16px',
                border: `1px solid ${alpha(GREEN_BP, 0.18)}`,
                bgcolor: alpha(CHAT_SURFACE, 0.6),
                transition: 'border-color 0.2s, box-shadow 0.2s',
                '&:focus-within': {
                  borderColor: alpha(GREEN_BP, 0.45),
                  boxShadow: `0 0 0 3px ${alpha(GREEN_BP, 0.1)}`,
                },
              }}
            >
              <TextField
                inputRef={inputRef}
                multiline
                minRows={1}
                maxRows={INPUT_MAX_ROWS}
                fullWidth
                placeholder={
                  historyLimitReached
                    ? 'Iniciá una nueva conversación para seguir'
                    : 'Escribí tu mensaje…'
                }
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, INPUT_MAX_CHARS))}
                onKeyDown={handleKeyDown}
                disabled={loading || historyLimitReached}
                autoComplete='off'
                variant='standard'
                inputProps={{ maxLength: INPUT_MAX_CHARS }}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                    py: 0.5,
                    px: 0.5,
                    maxHeight: INPUT_MAX_ROWS * 24 + 16,
                    overflowY: 'auto',
                    '& textarea': {
                      overflowY: 'auto !important',
                      resize: 'none',
                    },
                  },
                }}
              />
              <IconButton
                type='submit'
                disabled={loading || historyLimitReached || !input.trim()}
                aria-label='Enviar mensaje'
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  bgcolor: input.trim() ? GREEN_BP : alpha(GREEN_BP, 0.12),
                  color: input.trim() ? CREAM : alpha(GREEN_DARK, 0.35),
                  '&:hover': {
                    bgcolor: input.trim() ? darken(GREEN_BP, 0.08) : alpha(GREEN_BP, 0.18),
                  },
                  '&.Mui-disabled': {
                    bgcolor: alpha(GREEN_BP, 0.08),
                    color: alpha(GREEN_DARK, 0.25),
                  },
                }}
              >
                <SendRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            {input.length > 0 && (
              <Typography
                variant='caption'
                sx={{ display: 'block', mt: 0.75, px: 0.5, color: alpha(GREEN_DARK, 0.45), textAlign: 'right' }}
              >
                {input.length}/{INPUT_MAX_CHARS}
              </Typography>
            )}
          </Box>
        </Paper>
      </Fade>

      <IconButton
        key={launcherPopKey}
        aria-label={open ? 'Cerrar asistente' : 'Abrir asistente'}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: (theme) => theme.zIndex.modal + 1,
          width: 56,
          height: 56,
          bgcolor: GREEN_BP,
          color: CREAM,
          boxShadow: `0 8px 24px ${alpha(GREEN_DARK, 0.28)}`,
          animation: `${launcherPop} 0.5s cubic-bezier(0.34, 1.4, 0.64, 1)`,
          '&:hover': {
            bgcolor: darken(GREEN_BP, 0.06),
            boxShadow: `0 10px 28px ${alpha(GREEN_DARK, 0.32)}`,
          },
        }}
      >
        {open ? <CloseIcon /> : <ChatIcon />}
      </IconButton>
    </>
  );
}
